import { addItems, BuyableItemWithQuantity } from "@ac-expo/api";
import {
  BuyableItemCreationType,
  ComputablePricing,
  PartialCompleteBuyableItem,
} from "@ac-expo/api-types";
import { BasketType } from "@prisma/client";
import mitt from "mitt";

const GUEST_CART_STORAGE_KEY = "guest_cart_v1";

type GuestCartEvents = {
  change: GuestCartStorage;
};

export const guestCartEmitter = mitt<GuestCartEvents>();

export type GuestCartStorageItem = {
  key: string;
  quantity: number;
  buyableItem: PartialCompleteBuyableItem;
  pricing: ComputablePricing;
};

type GuestCartStorage = Partial<Record<BasketType, GuestCartStorageItem[]>>;

export type StorageAdapter = {
  getItem: (key: string) => string | null | undefined;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

let storageAdapter: StorageAdapter | null = null;

export const setGuestCartStorageAdapter = (adapter: StorageAdapter) => {
  storageAdapter = adapter;
};

const getStorage = (): StorageAdapter | null => {
  if (storageAdapter) return storageAdapter;
  if (typeof window !== "undefined" && typeof window.localStorage !== "undefined") {
    return {
      getItem: (key) => window.localStorage.getItem(key),
      setItem: (key, value) => window.localStorage.setItem(key, value),
      removeItem: (key) => window.localStorage.removeItem(key),
    };
  }
  return null;
};

const normalizeItems = (items: GuestCartStorageItem[]) => items.filter((item) => item.quantity > 0);

const readStorage = (): GuestCartStorage => {
  const storage = getStorage();
  if (!storage) return {};

  const raw = storage.getItem(GUEST_CART_STORAGE_KEY);
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as GuestCartStorage;
  } catch {
    return {};
  }
};

const writeStorage = (data: GuestCartStorage) => {
  const storage = getStorage();
  if (!storage) {
    return;
  }
  storage.setItem(GUEST_CART_STORAGE_KEY, JSON.stringify(data));
  guestCartEmitter.emit("change", data);
};

export const getGuestCartItems = (basketType: BasketType): GuestCartStorageItem[] => {
  const storage = readStorage();
  const items = normalizeItems(storage[basketType] ?? []);
  return items;
};

export const setGuestCartItems = (basketType: BasketType, items: GuestCartStorageItem[]) => {
  const storage = readStorage();
  const nextItems = normalizeItems(items);

  if (nextItems.length === 0) {
    delete storage[basketType];
  } else {
    storage[basketType] = nextItems;
  }

  writeStorage(storage);
  return nextItems;
};

export const mergeGuestCartItems = (basketType: BasketType, items: GuestCartStorageItem[]) => {
  const existingItems = getGuestCartItems(basketType);
  const mergedItems = new Map<string, GuestCartStorageItem>();

  existingItems.forEach((item) => mergedItems.set(item.key, item));
  items.forEach((item) => {
    const previous = mergedItems.get(item.key);
    const nextQuantity = (previous?.quantity ?? 0) + item.quantity;
    mergedItems.set(item.key, {
      ...item,
      quantity: nextQuantity,
    });
  });

  return setGuestCartItems(basketType, Array.from(mergedItems.values()));
};

export const clearGuestCartItems = (basketType: BasketType) => {
  setGuestCartItems(basketType, []);
};

export const syncGuestCartToServer = async (basketType: BasketType = "store"): Promise<boolean> => {
  const guestItems = getGuestCartItems(basketType);

  if (guestItems.length === 0) {
    return false;
  }

  const items: BuyableItemWithQuantity[] = guestItems.map((item) => ({
    buyableItem: item.buyableItem as BuyableItemCreationType,
    quantity: item.quantity,
  }));

  await addItems({ items }, basketType);
  clearGuestCartItems(basketType);

  return true;
};
